import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import client from '../api/client';
import { useAuth } from './AuthContext';
import { hasOnlyDataExchangeReaderRole } from '../config/roleAccess';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const notificationsEnabled = isAuthenticated && !hasOnlyDataExchangeReaderRole(user);
  const [unreadCount, setUnreadCount] = useState(0);

  const applyUnreadDelta = useCallback((delta) => {
    setUnreadCount((currentValue) => Math.max(0, currentValue + Number(delta || 0)));
  }, []);

  const markOneReadLocally = useCallback(() => {
    setUnreadCount((currentValue) => Math.max(0, currentValue - 1));
  }, []);

  const markAllReadLocally = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await client.get('/api/notifications/unread-count');
      setUnreadCount(Number(response.data?.unread || 0));
    } catch {
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (!notificationsEnabled) {
      setUnreadCount(0);
      return;
    }

    refresh();
    const intervalId = setInterval(refresh, 30000);
    return () => clearInterval(intervalId);
  }, [notificationsEnabled, refresh]);

  const value = useMemo(() => ({
    unreadCount,
    refresh,
    applyUnreadDelta,
    markOneReadLocally,
    markAllReadLocally,
  }), [unreadCount, refresh, applyUnreadDelta, markOneReadLocally, markAllReadLocally]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
